import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { FormikProps } from 'formik/dist/types';
import { ReactElement, SyntheticEvent, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { getSiteByUuid, getSiteDetailsById, updateSite } from '../../redux/slices/sites/sitesSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';

const NOTES_MAX = 1024;

interface IProp {
  open: boolean;
  siteId?: string;
  onClose: () => void;
}

interface Values {
  notes: string;
}

const schema = yup.object().shape({
  notes: yup.string().max(NOTES_MAX, `Should be ${NOTES_MAX} characters or fewer`),
});

export const SiteNotesForm = ({ open, siteId, onClose }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<Values>>(null);
  const site = useSelector(getSiteDetailsById(siteId));

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  const submit = async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
    if (!site?.id) return;
    try {
      setSubmitting(true);
      await dispatch(
        updateSite({
          id: site.id,
          organization_id: site.organization_id,
          name: site.name,
          ...(site.customer_id ? { customer_id: site.customer_id } : {}),
          // PUT /sites is a full replace: an omitted project_manager_id is treated
          // as null and clears the site's PM. Round-trip the existing value so a
          // notes edit never wipes the assigned project manager.
          ...(site.project_manager_id ? { project_manager_id: site.project_manager_id } : {}),
          site_type_id: site.site_type_id,
          address_1: site.address1,
          city: site.city,
          state: site.state,
          postal: site.postal,
          latitude: site.latitude?.toString() ?? '',
          longitude: site.longitude?.toString() ?? '',
          active: site.active ?? true,
          notes: values.notes,
        }),
      ).unwrap();
      await dispatch(getSiteByUuid(site.id));
      onClose();
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik innerRef={formikRef} validationSchema={schema} onSubmit={submit} enableReinitialize initialValues={{ notes: site?.notes || '' }}>
        {({ handleSubmit, handleChange, handleBlur, values, touched, errors, isSubmitting }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h3" component="div">
                  Job Site Notes
                </Typography>
                <IconButton onClick={handleClose}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <TextField
                name="notes"
                label="Notes"
                value={values.notes}
                error={Boolean(touched.notes && errors.notes)}
                helperText={(touched.notes && errors.notes) || `${values.notes.length}/${NOTES_MAX}`}
                onBlur={handleBlur}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={6}
                my={2}
                inputProps={{ maxLength: NOTES_MAX }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 6, pb: 6 }}>
              <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
